import styled from "styled-components";
import { useFetchEntities } from "@/reactQueryHooks";
import { v4 as uuidv4 } from "uuid";
import { ScheduleNote } from "./types";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  useUpdateScheduleNoteOfTourGroupById,
  useDeleteScheduleNoteOfTourGroupById,
} from "@/reactQueryHooks";
import moment from "moment";
import { useAxios } from "@/axiosProvider";
import { useState } from "react";

//------------------------------------------------------------------------------------

const NoteItem = styled.div`
  background-color: #f8f9fa;
  border-left: 3px solid #228be6;
  padding: 8px 12px;
  border-radius: 0 3px 3px 0;
  width: 100%;
  position: relative;
`;

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const NoteAuthor = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #495057;
`;

const NoteDate = styled.span`
  font-size: 10px;
  color: #868e96;
`;

const NoteBody = styled.div`
  font-size: 12px;
  color: #212529;
  white-space: pre-wrap;
  word-break: break-word;
`;

const IconContainer = styled.span`
  cursor: pointer;
  color: #868e96;
  font-size: 9px;
  &:hover {
    color: #228be6;
  }
`;

const NoteInput = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  font-size: 12px;
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
`;

const Button = styled.button`
  background-color: #228be6;
  color: white;
  border: none;
  border-radius: 3px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #1c7ed6;
  }

  &.cancel {
    background-color: #e9ecef;
    color: #495057;
    &:hover {
      background-color: #dee2e6;
    }
  }

  &.add {
    display: flex;
    align-items: center;
    font-size: 11px;
    padding: 4px 10px;

    &::before {
      content: "+";
      margin-right: 4px;
      font-size: 14px;
      font-weight: bold;
    }
  }
`;

//-------------------------------------------------------------------------------------

const ScheduleNoteItem = ({
  tourGroupId,
  note,
  refetch,
}: {
  tourGroupId: string;
  note: ScheduleNote;
  refetch: () => void;
}) => {
  const { data: entities } = useFetchEntities() as any;
  const users = entities?.data?.users || [];
  const noteAuthor = users?.find((user: any) => user._id === note.author_id);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(note.body);

  const { mutateAsync: updateNewScheduleNote } =
    useUpdateScheduleNoteOfTourGroupById() as any;

  const { mutateAsync: deleteScheduleNote } =
    useDeleteScheduleNoteOfTourGroupById() as any;

  return (
    <NoteItem key={note?._id || uuidv4()}>
      {isEditing ? (
        <>
          <NoteInput
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <ButtonContainer>
            <Button className="cancel" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                try {
                  await updateNewScheduleNote({
                    tour_group_id: tourGroupId,
                    note_id: note?._id,
                    note_body: inputValue,
                    author_id: "test",
                  });
                  refetch();
                } catch (error) {
                  alert("Error adding schedule note");
                  console.error("Error adding schedule note:", error);
                }
              }}
            >
              Save
            </Button>
          </ButtonContainer>
        </>
      ) : (
        <>
          <NoteHeader>
            <NoteAuthor>{noteAuthor?.name}</NoteAuthor>

            <div>
              <NoteDate>
                {moment(note.date).format("DD MMM YYYY, HH:mm")}
              </NoteDate>
            </div>
          </NoteHeader>
          <NoteBody>{note.body}</NoteBody>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <IconContainer onClick={() => setIsEditing(true)}>
              <EditIcon
                sx={{
                  fontSize: "15px",
                }}
              />
            </IconContainer>
            <IconContainer
              onClick={async () => {
                try {
                  await deleteScheduleNote({
                    tour_group_id: tourGroupId,
                    note_id: note?._id,
                  });
                  refetch();
                } catch (error) {
                  alert("Error adding schedule note");
                  console.error("Error adding schedule note:", error);
                }
              }}
            >
              <DeleteIcon
                sx={{
                  fontSize: "15px",
                }}
              />
            </IconContainer>
          </div>
        </>
      )}
    </NoteItem>
  );
};

export default ScheduleNoteItem;
